const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const EMBEDDED_GUID_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
};

function charFromCodePoint(codePoint: number): string {
  if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
    return "";
  }
  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return "";
  }
}

function decodeEntitiesOnce(value: string): string {
  return value
    .replace(/&([a-z]+);/gi, (match, name: string) => {
      return NAMED_ENTITIES[name.toLowerCase()] ?? match;
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      charFromCodePoint(Number.parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_, dec: string) =>
      charFromCodePoint(Number.parseInt(dec, 10)),
    );
}

function decodeLiteralUnicodeEscapes(value: string): string {
  return value.replace(/\\u([0-9a-f]{4})/gi, (_, hex: string) =>
    charFromCodePoint(Number.parseInt(hex, 16)),
  );
}

function decodePercentEncoding(value: string): string {
  if (!/%[0-9a-f]{2}/i.test(value)) {
    return value;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Plain text for a dropdown label: tags off, entities and escapes resolved. */
export function decodeDisplayText(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value !== "string") {
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const nested = record.text ?? record.name ?? record.label;
      if (nested != null && nested !== value) {
        return decodeDisplayText(nested);
      }
    }
    return "";
  }

  let text = value.replace(/<[^>]*>/g, " ");
  text = decodeLiteralUnicodeEscapes(text);
  text = decodePercentEncoding(text);
  for (let pass = 0; pass < 3; pass += 1) {
    const decoded = decodeEntitiesOnce(text);
    if (decoded === text) {
      break;
    }
    text = decoded;
  }

  return text
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(EMBEDDED_GUID_REGEX, " ")
    .replace(/\s+/g, " ")
    .replace(/\s*[-–—|,/]\s*$/g, "")
    .trim();
}

export function looksLikeGuid(value: string): boolean {
  return GUID_REGEX.test(value.trim());
}

/** A cosmos/legacy id, not a name a user can pick from. */
export function isOpaqueIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "[object Object]") {
    return true;
  }
  if (looksLikeGuid(trimmed)) {
    return true;
  }
  return /^\d+$/.test(trimmed);
}

/** Decoded label, or empty when the value is only an id or markup. */
export function toReadableLabel(value: unknown): string {
  const decoded = decodeDisplayText(value);
  if (!decoded || isOpaqueIdentifier(decoded)) {
    return "";
  }
  return decoded;
}

export type ContactOptionPerson = {
  name?: unknown;
  lastName?: unknown;
  mainOffice?: unknown;
  mainOfficeName?: unknown;
};

/** `[Full name] - [Main Office]`, omitting an unreadable office id. */
export function formatPersonWithMainOffice(
  person: ContactOptionPerson,
  officeLabel?: unknown,
): string {
  const personName = [person.name, person.lastName]
    .map((part) => toReadableLabel(part))
    .filter(Boolean)
    .join(" ");
  const office = toReadableLabel(officeLabel);
  if (personName && office) {
    return `${personName} - ${office}`;
  }
  return personName || office;
}
